/**
 *      Data-driven UI logic to display search results (snippets)
 *
 *      @author Irina Patrikeeva <irina.i.ipatrikeeva@nasa.gov>
 *
 *      Created on 08/29/2017
 *      Modified on 04/30/2018
 */


/**
 * Construct an array of search snippet items
 * @param jsonModel {object} - search UI model
 * @param recordCollection {BackboneCollection} - Backbone collection of search result data
 * @returns {Array}
 */
function getSnippetItems( jsonModel, recordCollection ) {
    var typeName,
        typeLabel,
        item,
        recordModel,
        titleObject,
        idObject,
        idModel,
        typeModel,
        titleModel,
        properties,
        propertiesObj,
        items = [];

    // Add title to the items array *first*
    // Add record title to the snippet items because "edi_title" is missing from the ElasticSearch data model (need to add it to ES)
    recordModel = lookupFieldModel( recordCollection, "edi_title" );
    if ( ! recordModel ) {
        recordModel = lookupFieldModel( recordCollection, "title" );
    }

    if ( recordModel ) {
        titleObject = {};
        titleObject[ "name" ] = "edi_title";
        titleObject[ "label" ] = "Title";
        titleObject[ "type" ] = "string";

        item = getPrimitiveItemFromObject( titleObject, recordModel.get( "value" ) );

        // Check if the search model properties contains the edi_title field
        titleModel = getFieldObjectByName( jsonModel, "edi_title" );

        if ( ! titleModel ) {
            item.hidden = true;
        }

        items.push( item );
    }

    // Add EDI ID item
    recordModel = lookupFieldModel( recordCollection, "id" );
    if ( recordModel ) {
        idObject = {};
        idObject[ "name" ] = "edi_id";
        idObject[ "label" ] = "ID";
        idObject[ "type" ] = "string";

        item = getPrimitiveItemFromObject( idObject, recordModel.get( "value" ) );

        // Check if the search model properties contains the edi_id field
        idModel = getFieldObjectByName( jsonModel, "edi_id" );

        if ( ! idModel ) {
           item.hidden = true;
        }
        items.push( item );
    }

    // Get model label and name from the top-level properties object
    if ( jsonModel ) {
        properties = jsonModel.properties;
        if ( properties && properties.length === 1 ) {

            propertiesObj = properties[ 0 ];

            if ( propertiesObj ) {

                typeName = propertiesObj.name;
                typeLabel = propertiesObj.label;
            }

            // Add EDI type item
            if ( typeName && typeLabel ) {

                item = { "field": "edi_type", "label": "Type", "type": "string", "value": typeLabel, "name": typeName };

                // Check if the search model properties contains the edi_type field
                typeModel = getFieldObjectByName( jsonModel, "edi_type" );
                if (!typeModel) {
                    item.hidden = true;
                }
                items.push(item);

            }
            else {
                // Add record type item to the snippet items from search response
                recordModel = lookupFieldModel( recordCollection, "type" );
                if (recordModel) {
                    item = {
                        "field": "edi_type",
                        "label": "Type",
                        "type": "string",
                        "value": recordModel.get("value"),
                        "name": recordModel.get("value")
                    };

                    // Check if the search model properties contains the edi_type field
                    typeModel = getFieldObjectByName(propertiesObj, "edi_type");
                    if (!typeModel) {
                        item.hidden = true;
                    }
                    items.push(item);
                }
            }

            items = addSearchModelProperties( items, propertiesObj, recordCollection );
        }
    }

    return items;
}

/**
 * Iterate through the properties array to get the display fields (could be deeply nested)
 * @param items {Array} - array of search snippet items
 * @param propertiesObj {object} - search UI model properties object
 * @param recordCollection {BackboneCollection} - Backbone collection of search result data
 * @returns {Array}
 */
function addSearchModelProperties( items, propertiesObj, recordCollection ) {

    // ES response object is nested in the same way as the search model
    var model,
        modelName,
        recordModel,
        item,
        urlModels = [],
        urlRecordModels = [],
        fields,
        nestedFields,
        propertiesCollection,
        i,
        j;

    if ( propertiesObj ) {

        propertiesCollection = getBackboneCollection( propertiesObj.properties );

        for ( j = 0; j < propertiesCollection.length; j++ ) {

            model = propertiesCollection.models[ j ];

            if ( model ) {
                modelName = model.get( "name" );
                // get value of the model from the recordCollection
                recordModel = lookupFieldModel( recordCollection, modelName );

                // Get fields from an array of fields nested fields or an array of fields
                if ( model.get("type") === "object" || model.get("multiple") ) {
                    fields = [];
                    nestedFields = getNestedFields( fields, model, recordModel, propertiesObj, recordCollection );

                    if ( nestedFields ) {
                        for ( i = 0; i < nestedFields.length; i++ ) {
                            items.push( nestedFields[ i ] );
                        }
                    }
                }
                else {
                    // Store field models that are links to display at the end of the search snippet
                    if ( model.get( "is_link" ) ) {
                        urlModels.push( model );
                        urlRecordModels.push( recordModel );
                    } else {
                        // Add primitive field other than title and type fields (they were added already)
                        if ( ! ( modelName === "title" || modelName === "edi_title" || modelName === "edi_type" ) ) {
                            item = getPrimitiveItem( model, recordModel );
                            if ( hasKeys( item ) && ! ( item.value === null || item.value === undefined ) ) {
                                items.push( item );
                            }
                        }
                    }
                }
            }
        }


        // Add URL items last
        for ( i = 0; i < urlModels.length; i++ ) {
            item = getLinkItem( urlModels[ i ], urlRecordModels[ i ], propertiesObj, recordCollection, false );
            if ( hasKeys( item ) && ! ( item.value === null || item.value === undefined ) ){
                item[  "url" ] = true;
                items.push( item );
            }
        }
    }

    return items;
}

/**
 * Create a JavaScript object that represents a record primitive field
 * @param model {object} - Backbone.Model object of the field
 * @param recordModel {undefined|BackboneModel} -  Backbone model of the field in the ES record
 * @returns {object}
 */
function getPrimitiveItem( model, recordModel ) {

    var item = {};

    if ( model && model.type !== "object" ) {
        item[ "field" ] = model.get( "name" );
        item[ "label" ] = model.get( "label" );
        item[ "type" ] = model.get( "type" );

        if ( recordModel ) {
            item[ "value" ] = recordModel.get("value");
        } else {
            item[ "value" ] = null;
        }

        if ( model.get( "has_viewer" ) ) {
            item[ "has_viewer" ] = model.get( "has_viewer" );
        }
        if ( model.get( "is_link" ) ) {
            item[ "is_link" ] = model.get( "is_link" );
        }
        if ( model.get( "is_email" ) ) {
            item[ "is_email" ] = model.get( "is_email" );
        }
        if ( model.get( "is_searchable" ) ) {
            item[ "is_searchable" ] = model.get( "is_searchable" );
        }
    }

    return item;
}

/**
 * Create a JavaScript object that represents a record primitive field
 * @param object {object} - field object
 * @param value {*} - field value (any primitive type)
 * @returns {object}
 */
function getPrimitiveItemFromObject( object, value ) {

    var item = {};

    if ( hasKeys( object ) ) {
        item[ "field" ] = object[ "name" ];
        item[ "label" ] = object[ "label" ];
        item[ "type" ] = object[ "type" ];
        item[ "value" ] = value;

        if ( object[ "has_viewer" ] ) {
            item[ "has_viewer" ] =  object[ "has_viewer" ];
        }
        if ( object[ "is_link" ] ) {
            item[ "is_link" ] =  object[ "is_link" ];
        }
        if ( object[ "is_email" ] ) {
            item[ "is_email" ] =  object[ "is_email" ];
        }
        if ( object[ "is_searchable" ] ) {
            item[ "is_searchable" ] =  object[ "is_searchable" ];
        }
    }

    return item;
}

/**
 * Recursively get deeply nested fields. Save only primitive field types (strings, numbers, booleans and dates)
 * @param fields {Array} - final array of fields with values
 * @param model {BackboneModel} - Backbone.Model object of the current search field
 * @param recordModel {BackboneModel} - Backbone model of the current field
 * @param propertiesObj {object} - search UI model properties object
 * @param recordCollection {BackboneCollection} - Backbone collection of search result data
 * @returns {Array}
 */
function getNestedFields( fields, model, recordModel, propertiesObj, recordCollection ) {

    var properties,
        isMultiple,
        type;

    if ( model ) {
        isMultiple = model.get( "multiple" );
        type = model.get( "type" );

        // Look for primitive fields in each of the item in the properties array
        properties = model.get( "properties" );

        // Nested field
        if ( type === "object" ) {
            if ( isMultiple ) {
                // Multiple nested objects
                fields = getMultipleNestedFields( fields, model, recordModel, propertiesObj, recordCollection );
            } else {
                // Single nested object
                fields = getSingleNestedField( fields, model, recordModel, propertiesObj, recordCollection );
            }
        } else {
            // Non-nested field (single or multiple)
            fields = getSingleNonNestedField( fields, model, recordModel, propertiesObj, recordCollection, false );
        }
    }

    return fields;
}

/**
 * Get primitive items from a single nested field object
 * @param fields {Array} - final array of fields with values
 * @param model {BackboneModel} - search UI Backbone model for the field
 * @param recordModel {BackboneModel} - ElasticSearch response Backbone model for the field
 * @param propertiesObj {object} - search UI field for the data type
 * @param recordCollection {BackboneCollection} - array of Backbone models of the ES response for all fields
 * @returns {Array}
 */
function getSingleNestedField( fields, model, recordModel, propertiesObj, recordCollection ) {

    var fieldName,
        fieldValue,
        valueModelArray,
        valueModelCollection,
        properties,
        property,
        propertyModel,
        isMultiple,
        isNested,
        propertyValueModel,
        hasParentBaseUrl,
        i;

    if ( model && model.get( "type" ) === "object" ) {

        fieldName = model.get( "name" );

        if ( fieldName && recordModel ) {
            fieldValue = recordModel.get( "value" );
            if ( fieldValue ) {

                // convert record's value into a Backbone model
                valueModelArray = getBackboneRecordModels( fieldValue ); // array of models
                valueModelCollection = new BackboneCollection;
                valueModelCollection.reset( valueModelArray );

                properties = model.get( "properties" );
                if ( properties ) {
                    // properties of the nested field
                    for ( i = 0; i < properties.length; i++ ) {
                        // Get field from each property
                        property = properties[ i ];
                        if ( property ) {

                            // convert property object into a Backbone model
                            propertyModel = getBackboneModel(property);

                            isMultiple = property.multiple;
                            isNested = property.type === "object";

                            // find the property's value model by name

                            propertyValueModel = lookupFieldModel( valueModelCollection, propertyModel.get( "name" ) );

                            if (isNested || isMultiple) {
                                fields = getNestedFields(fields, propertyModel, propertyValueModel, propertiesObj, recordCollection);
                            }
                            else {
                                // check if nested parent has a base_url
                                // if it does, set isNested to true
                                hasParentBaseUrl = false;

                                if ( model.get("base_url") ) {
                                    hasParentBaseUrl = true;
                                }

                                // get primitive value
                                fields = getSingleNonNestedField(fields, propertyModel, propertyValueModel, propertiesObj, recordCollection, hasParentBaseUrl);
                            }
                        }
                    }
                }
            }
        }
    }

    return fields;
}

/**
 * Add a primitive item to the final array of primitive fields
 * @param fields {Array} - final array of fields with values
 * @param model {BackboneModel} - search UI Backbone model for the field
 * @param recordModel {BackboneModel} - ElasticSearch response Backbone model for the field
 * @param propertiesObj {object} - search UI field for the data type
 * @param recordCollection {BackboneCollection} - array of Backbone models of the ES response for all fields
 * @param isNested {boolean} - flag if the field came from a nested object
 * @returns {Array}
 */
function getSingleNonNestedField( fields, model, recordModel, propertiesObj, recordCollection, isNested ) {

    var item,
        modelName;

    if ( model ) {
        modelName = model.get("name");

        if ( model.get( "is_link" ) ) {
            // Add link item
            item = getLinkItem( model, recordModel, propertiesObj, recordCollection, isNested );
        } else {
            // Add primitive field other than title and type fields (they were added already)
            if ( ! ( modelName === "title" || modelName === "edi_title" || modelName === "edi_type" ) ) {
                item = getPrimitiveItem( model, recordModel );
            }
        }
    }

    if ( hasKeys( item ) && ! ( item.value === null || item.value === undefined ) ) {
        fields.push( item );
    }

    return fields;
}

/**
 * Add primitive items to the final array from an array of nested field objects
 * @param fields {Array} - final array of fields with values
 * @param model {BackboneModel} - search UI Backbone model for the field
 * @param recordModel {BackboneModel} - ElasticSearch response Backbone model for the field
 * @param propertiesObj {object} - search UI field for the data type
 * @param recordCollection {BackboneCollection} - array of Backbone models of the ES response for all fields
 * @returns {Array}
 */
function getMultipleNestedFields( fields, model, recordModel, propertiesObj, recordCollection ) {

    var value,
        recordValueArray,
        recordValue,
        i;

    if ( model && model.get( "type" ) === "object" && model.get( "multiple" ) ) {

        // recordModel value is an array of objects nested inside an object
        // look at each item in the array
        if ( recordModel && typeof recordModel.get( "value" ) === "object" ) {
            recordValueArray = recordModel.get( "value" );

            if ( recordValueArray ) {

                for ( i = 0; i < recordValueArray.length; i++ ) {

                    recordValue = recordValueArray[i];

                    // set the value of the record model as the item's value
                    recordModel.set( "value", recordValue );
                    fields = getSingleNestedField( fields, model, recordModel, propertiesObj, recordCollection );
                }

            }

        }
    }

    return fields;
}